"use client"

import { useEffect, useState } from "react"
import { X, Repeat } from "lucide-react"
import { servicesAPI } from "../services/api"
import { useAuth } from "../context/AuthContext"

// Modal for the Convert-to-recurring action (issue #39). goscha's spec:
// "Add an option to convert one time job into recurring. We need to
// have a context menu where we can set up the schedule. The job itself
// should be duplicated, because a user should be able to change service
// option (for example from deep cleaning switch to regular) and change
// the price."
//
// So on confirm we POST /api/jobs/:id/duplicate with isRecurring=true +
// frequency + endDate + optional service/price overrides. The backend
// then creates the first recurring instance (and lazy-ahead instances
// per its existing recurring-duplicate path). The user lands on the
// new series' first job so they can tweak freely.

const FREQUENCIES = [
  { id: "weekly",    label: "Weekly" },
  { id: "biweekly",  label: "Every 2 weeks" },
  { id: "monthly",   label: "Monthly" },
  { id: "quarterly", label: "Every 3 months" },
]

export default function ConvertToRecurringModal({
  open,
  job,
  defaultDate,     // starts prefilled with the parent's date
  defaultTime,     // "HH:MM" | null
  busy,
  onClose,
  onConfirm,       // (payload) => Promise<void>
}) {
  const { user } = useAuth()
  const [startDate, setStartDate] = useState("")
  const [startTime, setStartTime] = useState("09:00")
  const [frequency, setFrequency] = useState("weekly")
  const [endDate, setEndDate] = useState("") // optional
  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)
  const [serviceId, setServiceId] = useState("")
  const [serviceName, setServiceName] = useState("")
  const [servicePrice, setServicePrice] = useState("")
  const [error, setError] = useState("")

  // Prefill from the parent job each time the modal opens.
  useEffect(() => {
    if (!open) return
    setError("")
    const d = defaultDate ? new Date(defaultDate) : null
    if (d && !isNaN(d.getTime())) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      setStartDate(`${y}-${m}-${dd}`)
      const hh = String(d.getHours()).padStart(2, "0")
      const mi = String(d.getMinutes()).padStart(2, "0")
      setStartTime(defaultTime || `${hh}:${mi}`)
    } else {
      const n = new Date()
      setStartDate(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`)
      setStartTime(defaultTime || "09:00")
    }
    setFrequency("weekly")
    setEndDate("")
    setServiceId(job?.service_id ? String(job.service_id) : "")
    setServiceName(job?.service_name || "")
    const parentPrice = Number(job?.service_price ?? job?.price ?? job?.total ?? 0)
    setServicePrice(parentPrice > 0 ? String(parentPrice) : "")
  }, [open, defaultDate, defaultTime, job])

  // Lazy-load the catalog when the modal first opens.
  useEffect(() => {
    if (!open || !user?.id || services.length > 0 || servicesLoading) return
    setServicesLoading(true)
    servicesAPI
      .getAll(user.id)
      .then((data) => {
        const list = Array.isArray(data) ? data : (data?.services || data?.data || [])
        setServices(list)
      })
      .catch(() => { /* fallback: no catalog, keep text field */ })
      .finally(() => setServicesLoading(false))
  }, [open, user?.id, services.length, servicesLoading])

  if (!open) return null

  const customerName =
    `${job?.customers?.first_name || ""} ${job?.customers?.last_name || ""}`.trim() ||
    job?.customer_name ||
    (job?.id ? `Job #${job.id}` : "this job")

  const handleServicePick = (id) => {
    setServiceId(id)
    if (id === "") return
    const s = services.find((x) => String(x.id) === String(id))
    if (!s) return
    setServiceName(s.name || serviceName)
    if (s.price != null && s.price !== "") setServicePrice(String(s.price))
  }

  const handleSubmit = async (e) => {
    e?.preventDefault?.()
    if (!startDate) return setError("Pick a start date for the recurring series.")
    if (!frequency) return setError("Pick a frequency.")
    if (endDate && endDate < startDate) return setError("End date can't be before the start date.")
    const priceNum = servicePrice === "" ? null : parseFloat(servicePrice)
    if (priceNum != null && (!Number.isFinite(priceNum) || priceNum < 0)) {
      return setError("Price must be a positive number (or leave blank to keep the parent's price).")
    }
    setError("")
    try {
      await onConfirm?.({
        isRecurring: true,
        frequency,
        endDate: endDate || null,
        scheduledDate: startDate,
        scheduledTime: startTime || "09:00",
        serviceId: serviceId ? Number(serviceId) : null,
        serviceName: serviceName || null,
        servicePrice: priceNum,
      })
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not convert to recurring.")
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10000] p-4"
      onClick={() => (busy ? null : onClose?.())}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-[var(--sf-text-primary)] flex items-center gap-2">
            <Repeat className="w-4 h-4 text-[var(--sf-blue-500)]" />
            Convert to recurring
          </h3>
          <button
            type="button"
            onClick={() => (busy ? null : onClose?.())}
            disabled={busy}
            className="text-[var(--sf-text-muted)] hover:text-[var(--sf-text-primary)] p-1 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-[12.5px] text-[var(--sf-text-secondary)] mb-5">
          Creates a recurring series starting from <strong>{customerName}</strong>'s job.
          The original one-time job stays. You can swap the service and change the price here.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
                First occurrence
              </label>
              <input
                type="date"
                required
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
                Time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sf-blue-500)]/40 focus:border-[var(--sf-blue-500)]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
              Frequency
            </label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
            >
              {FREQUENCIES.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-[var(--sf-text-secondary)] mb-1">
              End date <span className="text-[var(--sf-text-muted)] font-normal">(optional)</span>
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
            />
            <p className="text-[11px] text-[var(--sf-text-muted)] mt-1">
              Leave blank for an open-ended series. Instances are created a few months ahead and auto-extend as time passes.
            </p>
          </div>

          <div className="pt-2 border-t border-[var(--sf-border-light)]">
            <div className="text-xs font-semibold text-[var(--sf-text-secondary)] mb-2">
              Service &amp; price (optional override)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-[var(--sf-text-secondary)] mb-1">Service</label>
                {services.length > 0 ? (
                  <select
                    value={serviceId}
                    onChange={(e) => handleServicePick(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
                  >
                    <option value="">(keep: {job?.service_name || "parent's service"})</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}{s.price != null ? ` — $${s.price}` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                    placeholder={job?.service_name || "Service name"}
                    className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
                  />
                )}
              </div>
              <div>
                <label className="block text-xs text-[var(--sf-text-secondary)] mb-1">Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={servicePrice}
                  onChange={(e) => setServicePrice(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 text-sm border border-[var(--sf-border-light)] rounded-md bg-white"
                />
              </div>
            </div>
          </div>

          {error && <div className="text-xs text-red-600">{error}</div>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => (busy ? null : onClose?.())}
              disabled={busy}
              className="px-4 py-2 text-sm text-[var(--sf-text-secondary)] bg-white border border-[var(--sf-border-light)] rounded-md hover:bg-[var(--sf-bg-page)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="px-4 py-2 text-sm font-semibold text-white bg-[var(--sf-blue-500)] rounded-md hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Converting…" : "Create recurring series"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
